/**
 * Maximum cut quantity for the Reserve Material modal (PTB-6180).
 *
 * `reserve_material` places whole cuts on each bar separately and wastes the
 * remainder. Summing every available inch and dividing once over-counts,
 * because those remainders cannot be joined into another cut. The suggestion
 * has to pack bars the same way the reservation does, or the quantity it
 * offers is rejected.
 */

/** Material lengths are stored to 3 decimal places. */
const MILLI = 1000;

export function toMilliInches(value: number): number {
	if (!Number.isFinite(value)) return 0;
	return Math.round(value * MILLI);
}

/**
 * Whole cuts that fit in `available` inches after an optional one-time trim.
 * Floor division is done in thousandths of an inch so it matches the backend
 * `Decimal //` and does not drop a cut to a binary rounding error.
 */
export function cutsThatFit(
	available: number,
	cutUnit: number,
	trim = 0
): number {
	const room = toMilliInches(available) - toMilliInches(trim);
	const unit = toMilliInches(cutUnit);
	if (room <= 0 || unit <= 0) return 0;
	return Math.floor(room / unit);
}

export interface ReservableBar {
	total_available_length?: number | string | null;
	status?: string | null;
	inventory_location?: {
		shelf?: {
			reservations_enabled?: boolean | null;
			rack?: {
				reservations_enabled?: boolean | null;
				building?: { id?: string | null } | null;
			} | null;
		} | null;
	} | null;
}

/**
 * Bars `reserve_material` will cut from: located, not on quality hold or
 * scrapped, with length left, on a rack and shelf that allow reservations,
 * optionally limited to one building. Largest first, matching the backend
 * sort. Returns null when per-bar data is not loaded yet. A missing flag is
 * treated as enabled so older payloads still reserve.
 */
/** A missing flag is treated as enabled so older payloads still reserve. */
export function barReservationsEnabled(
	location: ReservableBar['inventory_location']
): boolean {
	const shelf = location?.shelf;
	if (shelf?.reservations_enabled === false) return false;
	if (shelf?.rack?.reservations_enabled === false) return false;
	return true;
}

export function reservableBarLengths(
	lengths: ReservableBar[] | null | undefined,
	buildingId?: string | null
): number[] | null {
	if (!Array.isArray(lengths)) return null;

	const bars: number[] = [];
	for (const bar of lengths) {
		if (bar?.status === 'Quality Hold' || bar?.status === 'Scrapped') {
			continue;
		}
		const available = Number(bar?.total_available_length ?? 0);
		if (!(available > 0)) continue;
		// Unlocated bars cannot be reserved; the floor has no place to find them.
		if (!bar?.inventory_location) continue;
		if (!barReservationsEnabled(bar.inventory_location)) continue;
		if (buildingId) {
			const barBuildingId =
				bar.inventory_location.shelf?.rack?.building?.id;
			if (barBuildingId !== buildingId) continue;
		}
		bars.push(available);
	}

	bars.sort((a, b) => b - a);
	return bars;
}

/**
 * Physical cuts that fit across separate bars. Trim is reserved once, on the
 * first bar that can still hold a cut after it. Bars that cannot hold that
 * cut are skipped and the trim stays pending, same as `reserve_material`.
 */
export function maxPhysicalCutsAcrossBars(
	barLengths: number[],
	cutUnit: number,
	trim: number
): number {
	// Largest first, same as reserve_material. Trim lands on the biggest bar
	// that can still hold a cut after it.
	const ordered = [...barLengths].sort((a, b) => b - a);
	let trimRemaining = trim;
	let total = 0;
	for (const available of ordered) {
		const cuts = cutsThatFit(available, cutUnit, trimRemaining);
		if (cuts <= 0) continue;
		trimRemaining = 0;
		total += cuts;
	}
	return total;
}

/**
 * The pre-PTB-6180 suggestion: one continuous length. Kept for a single
 * selected bar and Swiss. Multi-bar reserve does not use this while bars
 * are still loading; that sum over-counts what each bar can hold.
 */
export function continuousBaseCutQuantity(
	totalAvailable: number,
	cutUnit: number,
	trim: number,
	scrap: number
): number {
	if (!(cutUnit > 0)) return 0;
	return Math.max(0, Math.floor((totalAvailable - trim) / cutUnit) - (scrap || 0));
}

export function maximumBaseCutQuantity(args: {
	/** null when this reservation is not packed across bars. */
	barLengths: number[] | null;
	totalAvailable: number;
	cutUnit: number;
	trim: number;
	scrap: number;
}): number {
	if (args.barLengths === null) {
		return continuousBaseCutQuantity(
			args.totalAvailable,
			args.cutUnit,
			args.trim,
			args.scrap
		);
	}
	const physical = maxPhysicalCutsAcrossBars(
		args.barLengths,
		args.cutUnit,
		args.trim
	);
	return Math.max(0, physical - (args.scrap || 0));
}
